/* MDM — card rails.
 *
 * Turns any [data-mdm-rail] into a single-line horizontal scroller with
 * prev/next buttons. Navigation wraps: pressing next on the last card returns
 * to the first, so the rail never dead-ends in either direction.
 *
 * The cards are NOT duplicated in the DOM. A true marquee needs a second copy
 * of every node, which would repeat each ingredient name to screen readers and
 * to search engines. Wrapping the scroll position gives the same endless feel
 * without saying anything twice.
 */
/* Also reveals Material Symbols icons once the icon font is ready.
 * The ligature text ("local_shipping") would otherwise flash as literal words,
 * so mdm-product.css hides .mdm-icon until .fonts-ready lands on <html>.
 * A timeout reveals them anyway if the font never arrives, and a <noscript>
 * rule covers JS being off, so an icon can never stay permanently invisible.
 */
(function () {
  'use strict';

  function revealIcons() {
    document.documentElement.classList.add('fonts-ready');
  }

  if (document.fonts && document.fonts.load) {
    document.fonts
      .load("400 19px 'Material Symbols Rounded'", 'check')
      .then(revealIcons)
      .catch(revealIcons);
  } else {
    revealIcons();
  }
  window.setTimeout(revealIcons, 2500);

  var ARROW =
    '<svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="9 6 15 12 9 18"/></svg>';

  function build(rail) {
    if (rail.dataset.mdmRailReady) return;
    rail.dataset.mdmRailReady = '1';

    /* The wrapper is built here rather than in Liquid. Emitting an unclosed
     * <div> from one snippet and its </div> from another is a theme-check
     * error and breaks if a section's markup is ever reordered. Arrows and
     * edge fades need JS anyway; without JS the rail still scrolls, because
     * the overflow is pure CSS. */
    var wrap = document.createElement('div');
    wrap.className = 'mdm-rail-wrap';
    rail.parentNode.insertBefore(wrap, rail);
    wrap.appendChild(rail);

    function step() {
      var first = rail.firstElementChild;
      if (!first) return rail.clientWidth;
      var gap = parseFloat(getComputedStyle(rail).columnGap || '14') || 14;
      var card = first.getBoundingClientRect().width + gap;
      // move a whole number of cards, at least one, never past a full viewport
      var n = Math.max(1, Math.floor(rail.clientWidth / card));
      return card * n;
    }

    function maxScroll() {
      return rail.scrollWidth - rail.clientWidth;
    }

    function sync() {
      var max = maxScroll();
      var x = rail.scrollLeft;
      // 2px tolerance: sub-pixel layout means x rarely lands exactly on 0/max
      wrap.dataset.atStart = x <= 2 ? 'true' : 'false';
      wrap.dataset.atEnd = x >= max - 2 ? 'true' : 'false';
      // nothing to scroll: hide the whole apparatus
      wrap.dataset.static = max <= 2 ? 'true' : 'false';
    }

    /* Scrolling is driven by scrollTo() with an explicit behavior rather than
     * by a CSS `scroll-behavior` on the rail: setting it in CSS makes some
     * engines ignore programmatic scrolls on an overflow container entirely,
     * which leaves the arrows dead. Passing the behavior per call keeps
     * ordinary touch and trackpad scrolling untouched.
     *
     * Smooth scrolling is not universally honoured on overflow containers, so
     * one frame later we check whether the position actually moved and fall
     * back to a direct assignment if it did not. */
    function scrollToX(to) {
      var from = rail.scrollLeft;
      if (Math.abs(to - from) < 1) return;

      var smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      try {
        rail.scrollTo({ left: to, behavior: smooth ? 'smooth' : 'auto' });
      } catch (err) {
        rail.scrollLeft = to;
        return;
      }

      window.requestAnimationFrame(function () {
        if (Math.abs(rail.scrollLeft - from) < 1) {
          rail.scrollLeft = to;
        }
      });
    }

    function go(dir) {
      var max = maxScroll();
      var x = rail.scrollLeft;
      var target;
      if (dir > 0) {
        target = x >= max - 2 ? 0 : Math.min(max, x + step());
      } else {
        target = x <= 2 ? max : Math.max(0, x - step());
      }
      scrollToX(target);
    }

    ['prev', 'next'].forEach(function (kind) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'mdm-rail__nav mdm-rail__nav--' + kind;
      b.innerHTML = ARROW;
      b.setAttribute('aria-label', kind === 'prev' ? 'Previous' : 'Next');
      b.addEventListener('click', function () {
        go(kind === 'prev' ? -1 : 1);
      });
      wrap.appendChild(b);
    });

    rail.addEventListener('scroll', function () {
      window.requestAnimationFrame(sync);
    });

    rail.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        go(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        go(-1);
      }
    });

    if (window.ResizeObserver) {
      new ResizeObserver(sync).observe(rail);
    } else {
      window.addEventListener('resize', sync);
    }

    sync();
  }

  function init(root) {
    (root || document).querySelectorAll('[data-mdm-rail]').forEach(build);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // theme editor re-renders a section without reloading the page
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
